import { intlLocale } from '@/i18n'

/** Local calendar date as YYYY-MM-DD (no timezone shift). */
export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function todayIso(): string {
  return isoDate(new Date())
}

export function parseIso(value: string): Date {
  const [y = 1970, m = 1, d = 1] = value.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function isValidIsoDate(value: string | null): value is string {
  return !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(parseIso(value).getTime())
}

export function shiftMonth(month: string, delta: number): string {
  const d = parseIso(`${month}-01`)
  d.setMonth(d.getMonth() + delta)
  return isoDate(d).slice(0, 7)
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()

/** Intl formatter for the active interface language (cached per locale + options). */
function formatter(kind: 'month' | 'long' | 'short' | 'weekdayShort' | 'weekdayLong'): Intl.DateTimeFormat {
  const locale = intlLocale()
  const key = `${locale}|${kind}`
  let f = formatterCache.get(key)
  if (!f) {
    const options: Record<typeof kind, Intl.DateTimeFormatOptions> = {
      month: { month: 'long', year: 'numeric' },
      long: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
      short: { weekday: 'short', day: 'numeric', month: 'short' },
      weekdayShort: { weekday: 'short' },
      weekdayLong: { weekday: 'long' },
    }
    f = new Intl.DateTimeFormat(locale, options[kind])
    formatterCache.set(key, f)
  }
  return f
}

export const formatMonth = (month: string) => formatter('month').format(parseIso(`${month}-01`))
export const formatLongDay = (date: string) => formatter('long').format(parseIso(date))
export const formatShortDay = (date: string) => formatter('short').format(parseIso(date))

/** Weeks (Monday first) covering a month; null pads days outside it. */
export function monthGrid(month: string): (string | null)[][] {
  const first = parseIso(`${month}-01`)
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  const lead = (first.getDay() + 6) % 7
  const cells: (string | null)[] = Array.from({ length: lead }, () => null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${month}-${String(d).padStart(2, '0')}`)
  while (cells.length % 7 !== 0) cells.push(null)
  const weeks: (string | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

/** Weekday indexes (0 = Sunday), Monday first. */
const WEEKDAY_INDEXES_MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0]

/**
 * Weekday headers, Monday first, in the active interface language. Call at render time so the
 * names follow language changes. 2024-01-01 was a Monday.
 */
export function weekdaysMondayFirst(): { short: string; long: string; index: number }[] {
  return WEEKDAY_INDEXES_MONDAY_FIRST.map((index, i) => {
    const d = new Date(2024, 0, 1 + i)
    return { short: formatter('weekdayShort').format(d), long: formatter('weekdayLong').format(d), index }
  })
}
